import { inject, singleton } from "tsyringe";
import { ExtensionContext } from "../../common/models/extension-config";
import { Module } from "../../common/module";
import { CommonModule } from "../common.module";
import { ApiModule } from "../common/api/api.module";
import { RequestInterceptorModule } from "../common/request-interceptor.module";
import { UrlChangeListenerModule } from "../common/url-change-listener.module";
import { Subscription } from "rxjs";
@singleton()
export class NetflixLoginIntegrationModule extends Module {
  public name: string = "NetflixLoginIntegrationModule";
  public acceptedLicenseFlags: string[] = ["NetflixIntegrations"];
  public shouldRun(context: ExtensionContext): boolean {
    return context === ExtensionContext.vizportal;
  }
  constructor(
    @inject(CommonModule) private common: CommonModule,
    @inject(ApiModule) private apiModule: ApiModule,
    @inject(RequestInterceptorModule)
    private requestInterceptor: RequestInterceptorModule,
    @inject(UrlChangeListenerModule)
    private urlChangeListener: UrlChangeListenerModule
  ) {
    super([common, apiModule, urlChangeListener, requestInterceptor]);
  }

  private tryAgainButtonSelector =
    ".tb-empty-state-button,[data-tb-test-id=try-again-Button]";
  private msgContainerSelector =
    ".tb-login-msg-container>.tb-login-error,.tb-error-box-message";
  private titleSelector = ".tb-login-title";
  private displayRootSelector = "[role=alert][aria-atomic=true]";
  private hashTriggered = false;
  private hashChangeSub: Subscription = undefined;
  public async run(): Promise<void> {
    // There are 2 integrations to determine a user that does not exist:
    // 1. Integration into XmlHTTPRequests to determine when a 401 code is thrown for getSessionInfo OR an error code 4 is thrown for switchSite. When it's thrown, we store the window URL (it will be the original dashboard URL)
    // 2. The window location is checked at load time to determine when an user has no account / is unlicensed. When that happens, the activation flow is started with the use of the original dashboard URL captured when the 401 was thrown for getSessionInfo (step 1)

    this.requestInterceptor.onTableauRequest.subscribe((e) =>
      this.monitorHttpRequests(e.request, e.dataObject)
    );

    this.checkHash();

    this.hashChangeSub = this.urlChangeListener.event.subscribe(() =>
      this.checkHash()
    );
  }

  private checkHash() {
    if (this.hashTriggered) {
      return;
    }
    const hash = window.location.hash;
    if (!hash) {
      return;
    }
    this.log.i("[Netflix user activation] checkHash - hash:", hash);
    const hashRegex = /#\/signin\?redirect=.*/;
    if (hashRegex.test(hash)) {
      this.log.i("[Netflix user activation] checkHash - save the hash part to local storage:", hash);
      window.localStorage.setItem(
        "stm_savedHashUrlPart",
        JSON.stringify({
          urlPart: hash,
          timestamp: new Date().getTime(),
        })
      );
    }
    const r = /#\/error(\/signin)?\/(\d+)/.exec(hash);
    const hashCode = r && r[2] && +r[2];
    this.hashTriggered = true;
    this.log.i("[Netflix user activation] checkHash - hashCode:", hashCode);
    switch (hashCode) {
      case 10028:
      case 4:
        this.log.i("[Netflix user activation] Detected unlicensed URL.");
        this.handleUrl(window.location.hash, true);
        break;
      case 5:
      case 12:
        this.log.i("[Netflix user activation] Detected no existing user.");
        this.handleUrl(window.location.hash, false);
        break;
      default:
        this.hashTriggered = false;
        break;
    }
    if (this.hashTriggered) {
      this.hashChangeSub?.unsubscribe();
    }
  }

  private handledUrl = false;
  private async handleUrl(hash: string, isUnlicensed: boolean) {
    if (this.handledUrl) {
      return;
    }
    this.handledUrl = true;
    const tryAgainButton = await this.common.waitForElement(
      this.tryAgainButtonSelector,
      60,
      document
    );
    if (!tryAgainButton) {
      return;
    }
    const stm_type_regex = /stm_type=([^&]+)/.exec(hash);
    switch (stm_type_regex && stm_type_regex[1]) {
      case "error":
        var msg = decodeURIComponent(/stm_error=([^&]+)/.exec(hash)[1]);
        this.handleError("Activation failed", msg);
        break;
      case "no_sites":
        this.handleError(
          "Can not access Tableau Server",
          "You do not have access to any sites on this Server."
        );
        break;
      case "partial_error":
        this.handleError(
          "Activation ran into some problems",
          "Activation was partially successful, but we failed to activate you on some sites"
        );
        var stm_url_regex = /stm_url=([^&]+)/.exec(hash);
        if (stm_url_regex && stm_url_regex[1]) {
            this._addButton("Continue", decodeURIComponent(stm_url_regex[1]));
        }
        break;
      case "desired_site_not_activated":
        var stm_customMesage = /stm_customMessage=([^&]+)/.exec(hash);
        if (stm_customMesage && stm_customMesage[1]) {
            this.handleError(
            "",
            decodeURIComponent(stm_customMesage[1]).split("+").join(" "),
            "Continue",
            "/"
          );
        } else {
            this.handleError(
            "Activation failed",
            "You do not have access to this site. However, we activated you on other sites for your convenience",
            "Continue",
            "/"
          );
        }
        break;
      default:
        // do activation
        await this.common.requestAnimationFrame();
        this.handleUserActivation(false);
        
        // get Return URL (the last URL before the 401 getSessionInfo)
        let returnUrl = window.location.origin;
        const returnUrlItem = window.localStorage.getItem(
          "stm_tableauUrlBeforeLogin"
        );
        if (returnUrlItem) {
            const j = JSON.parse(returnUrlItem);
            this.log.i(`[Netflix user activation] Stored returnUrl before login: ${j.url}`);
          if (new Date().getTime() - j.timestamp < 300000) {
            returnUrl = j.url;
          }
        }
        // if neither "getSessionInfo" nor "switchSite" saved the return Dashboard URL to local storage, then the method could have been "refreshSession", 
        // in this case we failed to save the required Dashboard URL during the login process, so we check the value saved in the "checkHash" to see if it was saved there
        // and the final URL is manually put together based on this
        const returnUrlItemFromHashPart = window.localStorage.getItem(
          "stm_savedHashUrlPart"
        );
        try {
          if (returnUrlItemFromHashPart) {
            let jh;
            try {
              jh = JSON.parse(returnUrlItemFromHashPart);
            } catch (error) {
              this.log.e(`[Netflix user activation] Error parsing returnUrlItemFromHashPart: ${error}`);
              jh = null;
            }
       
            if (jh && jh.urlPart) {
              this.log.i(`[Netflix user activation] Stored returnUrl part before login: ${jh.urlPart}`);   
              if (new Date().getTime() - jh.timestamp < 300000) {
                try {
                  const baseOrigin = window.location.origin.endsWith("/") ? window.location.origin : window.location.origin + "/";
                  returnUrl = new URL(jh.urlPart, baseOrigin).href;
                } catch (urlError) {
                  this.log.e(`[Netflix user activation] Error constructing returnUrl: ${urlError}`);
                  returnUrl = window.location.origin;
                }
              }
            } else {
              this.log.w(`[Netflix user activation] Invalid or missing urlPart in stored returnUrl data.`);
              returnUrl = window.location.origin;
            }
          }
        } catch (unexpectedError) {
          this.log.e(`[Netflix user activation] Unexpected error: ${unexpectedError}`);
          returnUrl = window.location.origin;
        }      
        this.log.i(`[Netflix user activation] Final returnUrl after validation: ${returnUrl}`);        

        let site = "";
        let siteRegex = /\/site\/([^\/]+)/.exec(returnUrl);
        if (siteRegex && siteRegex[1]) {
          site = siteRegex[1];
        } else {
          siteRegex = /%2Fsite%2F(.*?)%2F/gm.exec(returnUrl);
          if (siteRegex && siteRegex[1]) {
            site = siteRegex[1];
          }
        }
        this.log.i(`[Netflix user activation] Extracted site: ${site}`);

        const errorPageUrl = window.location.href;
        const activationMode = isUnlicensed ? "createUser" : "reLicenseUser";
        var url = this.common.combineUrl(window.stm.config.appUrl,
          "/api/custom/nx/activate-user?ServerId=" +
            window.stm.config.serverId +
            "&ReturnUrl=" +
            encodeURIComponent(returnUrl) +
            "&TableauErrorPageUrl=" +
            encodeURIComponent(errorPageUrl) +
            "&ActivationMode=" +
            activationMode +
            "&TableauSiteUrl=" +
            site
        );
        this.log.i(`[Netflix user activation] Activation/Redirect URL: ${url}`);
        window.location.href = url;
        break;
    }
  }

  private monitorHttpRequests(request: XMLHttpRequest, postData: any) {
    if (postData?.method == "getSessionInfo") {
      const currentUrl = window.location.href;
      request.addEventListener(
        "load",
        () => {
          const resJson = JSON.parse(request.responseText);
          if (resJson?.result?.errors?.length) {
            var r = /#\/error(\/signin)?\/(\d+)/.exec(currentUrl);
            if (!r || !r[1]) {
              window.localStorage.setItem(
                "stm_tableauUrlBeforeLogin",
                JSON.stringify({
                  url: currentUrl,
                  timestamp: new Date().getTime(),
                })
              );
            }
          }
        },
        false
      );
    }
    if (postData?.method == "switchSite") {
      const currentUrl = window.location.href;
      request.addEventListener(
        "load",
        () => {
          const resJson = JSON.parse(request.responseText);
          if (resJson?.result?.errors?.some((err) => err?.code === 4)) {
            var r = /#\/error(\/signin)?\/(\d+)/.exec(currentUrl);
            if (!r || !r[1]) {
              window.localStorage.setItem(
                "stm_tableauUrlBeforeLogin",
                JSON.stringify({
                  url: currentUrl,
                  timestamp: new Date().getTime(),
                })
              );
            }
          }
        },
        false
      );
    }
  }


  private handleUserActivation(isUnlicensed: boolean) {
    this._removeButton();
    this._removeErrors();
    this._change_title();
    this._addLoading(isUnlicensed ? 'Activating your license...' : 'Activating your account...');
  }

  private handleError(title: string, message: string, buttonText?: string, buttonUrl?: string) {
    this._change_title(title);
    this._removeErrors();
    this._addError(message);
    this._removeButton();
    if (buttonText && buttonUrl) {
        this._addButton(buttonText, buttonUrl);
    }

}

private _removeButton() {
    const btn = this.common.queryElement(document.body, this.tryAgainButtonSelector);
    if (btn) {
        btn.style.display = "none";
    }
}

private _removeErrors() {
    const s = this.common.queryElements(document.body, this.msgContainerSelector);
    if (!s) {
        return;
    }
    for (let i = 0; i < s.length; i++) {
        if (i === 0) {
            s[i].innerHTML = '';
        } else {
            s[i].remove();
        }
    }
}

private _change_title(title?: string) {
    if (!this.common.queryElement(document.body, this.titleSelector)) {
        const titleDiv = document.createElement('div');
        titleDiv.className = 'tb-padded tb-login-title';
        this.common.queryElement(document.body, this.displayRootSelector).insertBefore(titleDiv, this.common.queryElement(document.body, this.displayRootSelector).children[0]);
    }

    if (title) {
        this.common.queryElement(document.body, this.titleSelector).style.display = 'block';
        this.common.queryElement(document.body, this.titleSelector).innerText = title;
    } else {
        this.common.queryElement(document.body, this.titleSelector).style.display = 'none';
    }
}
private _addError(msg) {
    const errors = this.common.queryElement(document.body, this.msgContainerSelector);
    if (!errors) {
        return [];
    }
    const node = document.createElement('div');
    node.classList.add('ng-binding');
    node.style.maxWidth = '400px';
    node.style.display = 'inline-block';
    node.innerHTML = msg;
    errors.appendChild(node);
}
private _addButton(msg, href) {
    var btn = this.common.queryElement(document.body, this.tryAgainButtonSelector);
    if (btn) {
        btn.style.display = 'inline-block';
    } else {
        btn = document.createElement('button');
        btn.classList.add('tb-empty-state-button');
        this.common.queryElement(document.body, this.displayRootSelector).parentElement.append(btn);
    }
    (btn as HTMLLinkElement).href = href;
    btn.innerText = msg;
    btn.onclick = function () {
        window.location.href = href;
    }
    
}

private loadingGif = '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" style="margin: auto; display: block; shape-rendering: auto;" width="24px" height="24px" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid">' +
    '<g transform="rotate(0 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="-0.9166666666666666s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<g transform="rotate(30 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="-0.8333333333333334s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<g transform="rotate(60 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="-0.75s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<g transform="rotate(90 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="-0.6666666666666666s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<g transform="rotate(120 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="-0.5833333333333334s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<g transform="rotate(150 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="-0.5s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<g transform="rotate(180 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="-0.4166666666666667s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<g transform="rotate(210 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="-0.3333333333333333s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<g transform="rotate(240 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="-0.25s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<g transform="rotate(270 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="-0.16666666666666666s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<g transform="rotate(300 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="-0.08333333333333333s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<g transform="rotate(330 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="0s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<!-- [ldio] generated by https://loading.io/ -->' +
    '</svg>';




    private loadingSnippet = '<style>' +
    '.stm-loading {' +
    'margin-top: 20px;' +
    '}' +
    '.stm-loading > .icon {' +
    'display: inline-block;' +
    'margin-right: 5px;' +
    'vertical-align: middle;' +
    '}' +
    '.stm-loading > .text {' +
    'display: inline-block;' +
    'vertical-align: middle;' +
    'font-size: 14px;' +
    '}' +
    '</style>' +
    '<div class="stm-loading">' +
    '<div class="icon">' +
    this.loadingGif +
    '</div>' +
    '<span class="text">Loading...</span>' +
        '</div>';
private loadingId = 'stm-loading';
private _addLoading(msg) {
    var msgContainer = this.common.queryElement(document.body, this.msgContainerSelector);
    var loadingDiv = document.createElement('div');
    var snippet = this.loadingSnippet;
    if (msg) {
        snippet = snippet.replace('Loading...', msg);
    }
    loadingDiv.innerHTML = snippet;
    loadingDiv.id = this.loadingId;
    msgContainer.parentElement.appendChild(loadingDiv);
}
private _removeLoading() {
    document.getElementById(this.loadingId).remove();
}
}
